import isiOS from '../helpers/isiOS';
import {
   first,
   createElement,
   isArray,
   isObject,
   head
} from '../utils';
import HtmlPlayer from './html';

const AUTO_LEVEL = { name: 'Auto' };

export default class ShakaPlayer extends HtmlPlayer {
   constructor(options, state) {
      super(options, state);
      this.logger.debug('shaka:player:created');
   }

   load({ src, type }) {
      src = isArray(src) ? head(src) : src;
      src = isObject(src) ? src.src : src;

      // If the source is a DASH stream
      // and the device is an iOS device, we need to convert it to HLS
      if (/format=mpd-time-csf/.test(src) && isiOS() && !this.options.disableAutoUrlRewrite) {
         type = 'application/x-mpegurl';
         src = src.replace('format=mpd-time-csf', 'format=m3u8-cmaf');
      }

      this.onError = this.onError.bind(this);

      this.logger.debug('shaka:player:loading', src);
      this._loadLibraryAndAttachMedia(src, type);
   }

   _loadLibraryAndAttachMedia(src, type) {
      // use requiresjs if we can
      if (typeof window.define === 'function' && window.define.amd) {
         window.require.config({
            paths: {
               shakajs: this.options.shakaURL
            }
         });

         window.require(['shakajs'], shaka => {
            window.shaka = shaka;
            this._attachMedia(src, type);
         });
      } else {
         const script = createElement('script', { src: this.options.shakaURL });
         const firstScriptTag = first(document.getElementsByTagName('script'));
         script.onload = script.onreadystatechange = () => this._attachMedia(src, type);

         if (!firstScriptTag.parentNode) return;

         firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
      }
   }

   async _attachMedia(src, type) {
      let shakaOptions = this.options.shaka;
      window.shaka.polyfill.installAll();

      if (window.shaka.Player.isBrowserSupported()) {
         this.shaka = new window.shaka.Player();

         if (this.options.shaka) {
            this.shaka.configure(this.options.shaka);
         }

         this.shaka.setTextTrackVisibility(!!this.options.showSubtitles);

         this.preselectQuality();
         this.modifyRequests(src);

         await this.configureDRM();
         await this.shaka.attach(this.$el);
         await this.shaka.load(src, null, type).catch(this.onError);

         // Catching errors after load
         const listener = new window.shaka.util.EventManager();
         listener.listen(this.shaka, 'error', (e) => this.onError(e.detail));

         this.shaka.configure(shakaOptions);
      } else {
         this.logger.error('Browser not supported!');
      }
   }

   async configureDRM() {
      const servers = {};

      if (this.options.widevineURL) {
         servers['com.widevine.alpha'] = this.options.widevineURL;
      }

      if (this.options.playreadyURL) {
         servers['com.microsoft.playready'] = this.options.playreadyURL;
      }

      if (this.options.fairplayURL) {
         servers['com.apple.fps.1_0'] = this.options.fairplayURL;
      }

      this.shaka.configure({ drm: { servers } });

      if (this.options.fairplayCertificateUrl) {
         const req = await fetch(this.options.fairplayCertificateUrl);
         const cert = await req.arrayBuffer();

         this.shaka.configure('drm.advanced.com\\.apple\\.fps\\.1_0.serverCertificate', new Uint8Array(cert));

         window.shaka.polyfill.PatchedMediaKeysApple.install();

         const FairPlayUtils = window.shaka.util.FairPlayUtils;
         this.shaka.getNetworkingEngine().registerRequestFilter(FairPlayUtils.ezdrmFairPlayRequest);
         this.shaka.getNetworkingEngine().registerResponseFilter(FairPlayUtils.commonFairPlayResponse);
         this.shaka.configure('drm.initDataTransform', FairPlayUtils.ezdrmInitDataTransform);
      }
   }

   onError(event) {
      this.bus.emit('error', event);
      this.logger.error(event);
   }

   modifyRequests(src) {
      this.shaka.getNetworkingEngine().registerRequestFilter((type, request) => {
         if (type === window.shaka.net.NetworkingEngine.RequestType.KEY && this.options.aesToken) {
            request.headers.Authorization = 'Bearer ' + this.options.aesToken;
         }

         const params = src?.split('?')[1]?.split('&');

         // Append query parameters in case when token is required in the query
         // check query parameters and if request uri don't contain it add it
         if (params?.length) {
            params.forEach((value) => {
               const requestHadParams = request.uris[0]?.split('?')[1];

               if (!requestHadParams) {
                  request.uris[0] += '?' + value;
               } else if (!request.uris[0].includes(value)) {
                  request.uris[0] += '&' + value;
               }
            });
         }
      });
   }

   preselectQuality() {
      if (!this.options.qualityLevel) {
         return;
      }

      const eventManager = new window.shaka.util.EventManager();

      eventManager.listen(this.shaka, 'streaming', () => {
         const h = parseInt(this.options.qualityLevel, 10);
         const level = this.getLevels().find(l => l.height === h);

         if (level) {
            this.setLevel(level);
         }
      });

   }

   getLevels() {
      return [AUTO_LEVEL].concat(this.shaka.getVariantTracks());
   }

   setLevel(level) {
      if (level === AUTO_LEVEL) {
         this.shaka.configure({ abr: { enabled: true } });
      } else {
         this.shaka.configure({ abr: { enabled: false } });
         this.shaka.selectVariantTrack(level, true);
      }
   }

   goToLive() {
      this.shaka.goToLive();
   }

   isLive() {
      return this.$el.currentTime >= this.shaka.seekRange().end - 10;
   }

   getLevel() {
      const track = this.shaka.getVariantTracks().find(track => track.active);
      return this.shaka.getConfiguration().abr.enabled ? AUTO_LEVEL : track;
   }

   isMultiQuality() {
      return this.shaka.getVariantTracks().length > 1;
   }

   destroy() {
      this.shaka.destroy();
      super.destroy();
   }
}
